import { Article, ArticleBlock, ArticleMeta } from 'types/blog';
import { ARTICLE_META } from './meta';

// ==============================|| ブログの入口 ||============================== //
//
// 記事データは2つに分かれている。
//   ./meta.ts     … 全記事の見出し（本文なし）。一覧・カテゴリ一覧・関連記事が使う
//   ./registry.ts … 本文。記事ページが、開かれた1本だけを動的に読み込む
//
// ここはその2つを束ねる場所で、両方から使う小さな道具（パスの組み立て、日付の整形、
// 読了目安の計算）を置いている。
//
// ★ ここから本文(./articles/<slug>)を静的 import しないこと。
//   このファイルは一覧ページも読み込むため、全記事の本文がバンドルに入ってしまう。
//
// 記事を増やす手順は ./meta.ts のコメントにまとめてある。

export { ARTICLE_META };
export { hasArticle, getLoadedArticle, loadArticle } from './registry';

/** /blog/<slug> のパスを組み立てる。リンク先を手書きしないための唯一の入口 */
export const articlePath = (slug: string) => `/blog/${slug}`;

/**
 * slug から記事の見出しを引く。見つからなければ undefined(ページ側で404を出す)。
 *
 * 返すのはメタだけで、本文は入っていない。本文が要るのは記事ページだけなので、
 * そちらは loadArticle()/getLoadedArticle() を使う。
 */
export const findArticleMeta = (slug?: string): ArticleMeta | undefined => ARTICLE_META.find((meta) => meta.slug === slug);

/**
 * 関連記事を引く。カードに出すだけなのでメタを返す。
 * related に存在しない slug が書かれていても落ちないよう、引けたものだけを返す
 * (記事を下書き段階で参照することがあるため)。
 */
export const findRelated = (article: Article): ArticleMeta[] =>
    (article.related ?? []).map((slug) => findArticleMeta(slug)).filter((found): found is ArticleMeta => Boolean(found));

/** 'YYYY-MM-DD' を「2026年9月14日」の形にする。本文の表示用 */
export const formatDate = (date: string) => {
    const [year, month, day] = date.split('-');
    return `${year}年${Number(month)}月${Number(day)}日`;
};

/** 1分あたりに読める日本語の文字数。速い人と遅い人の間を取った概算値 */
const CHARS_PER_MINUTE = 600;

/**
 * 読了目安(分)を記事データから概算する。
 *
 * 記事ごとに手で書くと更新のたびにずれるため、本文の文字数から毎回計算する。
 * 表や見出しも含めて数えているので厳密ではないが、
 * 「5分で読めるのか、20分かかるのか」が伝われば目的は果たせる。
 */
export const estimateReadingMinutes = (article: Article): number => {
    const collect = (block: ArticleBlock): string[] => {
        switch (block.type) {
            case 'p':
            case 'h3':
                return [block.text];
            case 'list':
                return block.items;
            case 'table':
                return [...block.head, ...block.rows.flat(), block.caption ?? ''];
            case 'note':
                return [block.title ?? '', ...block.body];
            case 'example':
                return [block.title, ...block.body];
            case 'compare':
                return [block.title ?? '', block.bad, block.badReason, block.good, block.goodReason];
            case 'steps':
                return block.items.flatMap((item) => [item.title, item.body]);
            case 'faq':
                return block.items.flatMap((item) => [item.q, item.a]);
            // CTA は読み物ではないので数えない
            default:
                return [];
        }
    };

    const characters = [
        ...article.lead,
        ...article.summary,
        ...article.sections.flatMap((section) => [section.heading, ...section.blocks.flatMap(collect)])
    ].join('').length;

    return Math.max(1, Math.round(characters / CHARS_PER_MINUTE));
};
